import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { UserPlus, UserCheck, RefreshCw } from "lucide-react";

export default function FollowButton({ target, onChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [following, setFollowing] = useState(!!target.is_following);
  const [count, setCount] = useState(target.follower_count || 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const followsYou = !!target.follows_you;

  const toggle = async () => {
    if (!user) { nav("/login"); return; }
    if (target.id === user.id) return;
    setBusy(true); setErr("");
    try {
      if (following) {
        await api.delete(`/follow/${target.id}`);
        setFollowing(false); setCount(Math.max(0, count - 1));
        onChange?.({ following: false, count: Math.max(0, count - 1) });
      } else {
        await api.post(`/follow/${target.id}`);
        setFollowing(true); setCount(count + 1);
        onChange?.({ following: true, count: count + 1 });
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (target.id === user?.id) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-[#4A4A4A]" data-testid="follow-self">
        <UserCheck size={14}/> {count} {count === 1 ? "follower" : "followers"}
      </span>
    );
  }

  const label = following ? "Following" : (followsYou ? "Follow back" : "Follow");
  const Icon = following ? UserCheck : (followsYou ? RefreshCw : UserPlus);

  return (
    <div className="inline-flex items-center gap-3">
      <button onClick={toggle} disabled={busy}
        data-testid={following ? "unfollow-btn" : (followsYou ? "follow-back-btn" : "follow-btn")}
        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition-all
          ${following
            ? "bg-white text-[#0A0A0A] border-[#E5E5E3] hover:bg-[#F2F2F0]"
            : "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:bg-[#1F1F1F]"}`}>
        <Icon size={14}/>
        {label}
      </button>
      {err && <span className="text-xs text-[#B84B4B]">{err}</span>}
    </div>
  );
}
